import { ChevronUp, MoreHorizontal } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/design-system';
import {
  SIZE_SCALE,
  GAP_SCALE,
  TYPE_SCALE,
  PADDING_X,
  RADIUS_SCALE,
} from '@/components/design-system/tokens';
import {
  PAGINATION_CONTROL_BUTTON,
  PAGINATION_NUMBER_BUTTON,
} from '@/components/ui/PaginationStyles';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';

interface PaginationButtonsProps {
  currentPage: number;
  totalPages: number;
  pageNumbers: number[];
  setCurrentPage: (page: number | ((prev: number) => number)) => void;
}

interface TranslationFunction {
  (
    page: string,
    key: string,
    fallback?: string,
    variables?: Record<string, string | number | boolean>
  ): string;
}

export function PaginationButtons({
  currentPage,
  totalPages,
  pageNumbers,
  setCurrentPage,
}: PaginationButtonsProps) {
  const { t } = useTranslation();
  const tSafe = t as TranslationFunction;

  const paginationInstructionsId = 'inquiries-pagination-instructions';

  // Truncate page numbers to show max 5 pages with ellipsis
  const maxPagesToShow = 5;
  const truncatedPageNumbers = React.useMemo(() => {
    if (totalPages <= maxPagesToShow) {
      return pageNumbers;
    }

    const pages = [];
    const half = Math.floor(maxPagesToShow / 2);
    let start = Math.max(1, currentPage - half);
    let end = start + maxPagesToShow - 1;

    if (end > totalPages) {
      end = totalPages;
      start = end - maxPagesToShow + 1;
    }

    if (start < 1) {
      start = 1;
      end = maxPagesToShow;
    }

    // Add ellipsis logic
    for (let i = start; i <= end; i++) {
      if (i === start && i > 1) {
        pages.push('ellipsis-start');
      }
      pages.push(i);
      if (i === end && i < totalPages) {
        pages.push('ellipsis-end');
      }
    }

    return pages;
  }, [pageNumbers, currentPage, totalPages, maxPagesToShow]);

  return (
    <>
      <p className="sr-only" id={paginationInstructionsId}>
        {tSafe(
          'admin_inquiries',
          'pagination_keyboard_help',
          'Press left or right arrow keys to change pages.'
        )}
      </p>
      <div
        className={`flex items-center justify-center ${GAP_SCALE['1']} sm:${GAP_SCALE['2']} flex-wrap focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 ${RADIUS_SCALE.xl}`}
        role="toolbar"
        aria-label={tSafe('admin_inquiries', 'pagination_controls', 'Inquiry pagination controls')}
        aria-describedby={paginationInstructionsId}
      >
        {/* First & Previous */}
        <Button
          variant="pagination"
          radius="xl"
          size="sm"
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          className={cn(
            PAGINATION_CONTROL_BUTTON,
            `hidden sm:flex ${SIZE_SCALE['7']} sm:${SIZE_SCALE['9']} ${PADDING_X['2']} sm:${PADDING_X['3']} ${TYPE_SCALE['3xs']} sm:${TYPE_SCALE.xs} md:${TYPE_SCALE.sm}`
          )}
        >
          {tSafe('admin_inquiries', 'first', 'First')}
        </Button>
        <Button
          variant="pagination"
          radius="xl"
          size="sm"
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
          className={cn(
            PAGINATION_CONTROL_BUTTON,
            `${SIZE_SCALE['7']} sm:${SIZE_SCALE['9']} ${PADDING_X['1.5']} sm:${PADDING_X['2']} md:${PADDING_X['3']} ${TYPE_SCALE['3xs']} sm:${TYPE_SCALE.xs} md:${TYPE_SCALE.sm}`
          )}
        >
          <ChevronUp
            className={`${SIZE_SCALE['3.5']} sm:${SIZE_SCALE['4']} rotate-[-90deg] sm:hidden`}
          />
          <span className="hidden sm:inline">
            {tSafe('admin_inquiries', 'previous', 'Previous')}
          </span>
        </Button>

        {/* Page Numbers */}
        <div className={`flex items-center ${GAP_SCALE['1']}`}>
          {truncatedPageNumbers.map(pageNum => {
            if (pageNum === 'ellipsis-start' || pageNum === 'ellipsis-end') {
              return (
                <div
                  key={pageNum}
                  className={`flex items-center justify-center ${SIZE_SCALE['8']} sm:${SIZE_SCALE['8']} md:${SIZE_SCALE['9']} min-w-[2.25rem] sm:${SIZE_SCALE.w8} md:${SIZE_SCALE.w9}`}
                  aria-hidden="true"
                >
                  <MoreHorizontal className={`${SIZE_SCALE['3.5']} sm:${SIZE_SCALE['4']}`} />
                  <span className="sr-only">More pages</span>
                </div>
              );
            }

            return (
              <Button
                key={pageNum}
                variant={currentPage === pageNum ? 'paginationActive' : 'pagination'}
                radius="xl"
                size="sm"
                onClick={() => setCurrentPage(pageNum as number)}
                className={cn(
                  PAGINATION_NUMBER_BUTTON,
                  `${SIZE_SCALE['8']} sm:${SIZE_SCALE['8']} md:${SIZE_SCALE['9']} min-w-[2.25rem] sm:${SIZE_SCALE.w8} md:${SIZE_SCALE.w9} ${TYPE_SCALE['2xs']} sm:${TYPE_SCALE.xs} md:${TYPE_SCALE.sm}`
                )}
                aria-current={currentPage === pageNum ? 'page' : undefined}
              >
                {pageNum}
              </Button>
            );
          })}
        </div>

        <Button
          variant="pagination"
          radius="xl"
          size="sm"
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages}
          className={cn(
            PAGINATION_CONTROL_BUTTON,
            `${SIZE_SCALE['7']} sm:${SIZE_SCALE['9']} ${PADDING_X['1.5']} sm:${PADDING_X['2']} md:${PADDING_X['3']} ${TYPE_SCALE['3xs']} sm:${TYPE_SCALE.xs} md:${TYPE_SCALE.sm}`
          )}
        >
          <ChevronUp className={`${SIZE_SCALE['3.5']} sm:${SIZE_SCALE['4']} rotate-90 sm:hidden`} />
          <span className="hidden sm:inline">{tSafe('admin_inquiries', 'next', 'Next')}</span>
        </Button>
        <Button
          variant="pagination"
          radius="xl"
          size="sm"
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          className={cn(
            PAGINATION_CONTROL_BUTTON,
            `hidden sm:flex ${SIZE_SCALE['7']} sm:${SIZE_SCALE['9']} ${PADDING_X['2']} sm:${PADDING_X['3']} ${TYPE_SCALE['3xs']} sm:${TYPE_SCALE.xs} md:${TYPE_SCALE.sm}`
          )}
        >
          {tSafe('admin_inquiries', 'last', 'Last')}
        </Button>
      </div>
    </>
  );
}
